/**
 * @file InvoiceEditOfferDialog.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-09
 * @modified 2026-10-09
 *
 * 1.1.0 (2026-10-09, review): the supplier reference is offered too; a line
 * added to the order after invoicing is a notice; "Leave the invoice" says it
 * can be offered again from the order window ("Update invoice from order…",
 * UpdateInvoiceFromOrderDialog).
 * 1.2.0 (review): each proposal goes back with the server's `token`, so only
 * exactly what was shown can be applied.
 *
 * An invoiced order corrected after delivery (Cam, 9 Oct 2026): "offer to
 * change the invoice if it is not behind the accounting lock, otherwise
 * advise the user". After the order (or PO) save, the server's `invoice_offer`
 * is shown here — each invoice line's words, item code and account, old → new
 * (services/invoiceEditOfferService). Never automatic:
 *
 *   - "Apply these changes to INV-…" edits the invoice through the ordinary
 *     invoice edit (POST /api/orders/customer-orders/:id/invoice-edit-offer/apply,
 *     the posted-edit patch for one in Xero), one invoice at a time;
 *   - "Leave the invoice as it is" changes nothing;
 *   - an invoice that cannot be changed here (the lock, a payment, a till
 *     invoice, no 'Edit posted documents') is a notice saying why and what to
 *     do instead, with nothing to apply.
 *
 * An invoice that had gone out to the customer says so; once it is changed the
 * revised copy is offered ("Revised" on the PDF and subject, to whoever had it
 * last — POST /api/invoices/:id/send-email { revised: true }).
 */

import React, { useState } from 'react';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { offerLineText, offerNotices } from '../utils/afterDelivery';
import './BillPoCostDialog.css';

function OfferBody({ offer, busy, error }) {
  useSlideOutButton([busy, (offer.proposals || []).length]);
  const proposals = offer.proposals || [];
  const notices = offerNotices(offer);
  return (
    <div className="invoice-proposal-body">
      {proposals.length > 0 && (
        <p className="invoice-proposal-intro">
          The order was invoiced before this correction. These are the changes the invoice needs to match it —
          words, item codes, the item's sales account and the supplier reference only; quantities, prices, VAT and totals stay as they are.
        </p>
      )}
      {proposals.map(p => (
        <div key={p.invoice_id} className="invoice-proposal-invoice">
          <h4>{p.invoice_no}{p.in_xero ? ' · in Xero' : ''}</h4>
          {p.gone_out && (
            <p className="invoice-proposal-hint">
              <span className="material-symbols-rounded">outgoing_mail</span>
              It has gone out to the customer ({p.gone_out}). Once it is changed you will be offered to send them the revised copy.
            </p>
          )}
          <div className="table-container">
            <table className="data-table invoice-proposal-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map(l => (
                  <tr key={l.invoice_line_id}>
                    <td>{l.product || l.old?.description || l.old?.item_code || '—'}</td>
                    <td>{offerLineText(l)}</td>
                  </tr>
                ))}
                {p.header?.supplier_reference && (
                  <tr key="supplier-reference">
                    <td>Supplier reference</td>
                    <td>{p.header.supplier_reference.old || 'none'} → {p.header.supplier_reference.new || 'none'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {notices.map((text, i) => (
        <p key={`n-${i}`} className="invoice-proposal-hint">
          <span className="material-symbols-rounded">info</span>{text}
        </p>
      ))}
      {proposals.some(p => p.in_xero) && (
        <p className="invoice-proposal-note">An invoice in Xero is changed there too, keeping each line, with the reason on its history.</p>
      )}
      {error && (
        <div className="bill-po-cost-error" role="alert">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {{ proposals: object[], notices: object[] }} props.offer — the save's `invoice_offer`
 * @param {number} props.orderId
 * @param {string} [props.reason] — the edit's reason, kept with the invoice change
 * @param {(message: string|null, type?: string) => void} props.onDone — called once, when finished
 */
function InvoiceEditOfferDialog({ offer, orderId, reason = '', onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resendQueue, setResendQueue] = useState(null); // [{ invoice_id, invoice_no, to }]
  const [summary, setSummary] = useState([]);
  const proposals = offer?.proposals || [];
  const numbers = proposals.map(p => p.invoice_no).filter(Boolean);

  const finish = (parts, type = 'success') => onDone(parts.filter(Boolean).join(' ') || null, type);
  const leftText = proposals.length
    ? `${numbers.join(', ')} left as ${numbers.length > 1 ? 'they were' : 'it was'} — "Update invoice from order…" in the order window offers the changes again.`
    : null;

  const apply = async () => {
    setBusy(true);
    setError(null);
    const done = [];
    const failed = [];
    const resend = [];
    for (const p of proposals) {
      try {
        const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/invoice-edit-offer/apply`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ proposal: { invoice_id: p.invoice_id, lines: p.lines, header: p.header || null, token: p.token }, reason })
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || !d.success) { failed.push(`${p.invoice_no} was NOT changed: ${d.error || 'it could not be changed'}`); continue; }
        done.push(d.message || `${p.invoice_no} updated.`);
        if (d.resend && Array.isArray(d.resend.to) && d.resend.to.length) resend.push({ invoice_id: d.invoice_id, invoice_no: d.invoice_no, to: d.resend.to });
      } catch (err) {
        failed.push(`${p.invoice_no} was NOT changed: ${err.message}`);
      }
    }
    setBusy(false);
    const parts = [...done, ...failed];
    if (resend.length) {
      setSummary(parts);
      setResendQueue(resend);
      return;
    }
    finish(parts, failed.length ? 'warning' : 'success');
  };

  const sendRevised = async () => {
    const [first, ...rest] = resendQueue;
    let note;
    try {
      const res = await fetch(`${API_URL}/api/invoices/${first.invoice_id}/send-email`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: first.to.join(', '), revised: true })
      });
      const d = await res.json().catch(() => ({}));
      note = res.ok && d.success
        ? `The revised copy of ${first.invoice_no} was sent to ${first.to.join(', ')}.`
        : `The revised copy of ${first.invoice_no} was not sent: ${d.error || 'it could not be sent'} — send it from Search Invoices.`;
    } catch (err) {
      note = `The revised copy of ${first.invoice_no} was not sent: ${err.message} — send it from Search Invoices.`;
    }
    const next = [...summary, note];
    if (rest.length) { setSummary(next); setResendQueue(rest); return; }
    finish(next);
  };

  const skipRevised = () => {
    const [first, ...rest] = resendQueue;
    const next = [...summary, `${first.invoice_no}'s revised copy was not sent — send it from Search Invoices when you are ready.`];
    if (rest.length) { setSummary(next); setResendQueue(rest); return; }
    finish(next);
  };

  if (resendQueue && resendQueue.length) {
    const r = resendQueue[0];
    return (
      <ConfirmDialog
        isOpen
        type="info"
        title="Send the customer the revised copy?"
        message={`${summary.join(' ')} ${r.invoice_no} had gone to ${r.to.join(', ')}. Send them the revised copy now? It says "Revised" in the subject and on the PDF.`}
        confirmText="Send revised copy"
        cancelText="Not now"
        onConfirm={sendRevised}
        onCancel={skipRevised}
      />
    );
  }

  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : () => finish([leftText], 'info')}
      title={proposals.length ? 'Change the invoice to match?' : 'The invoice'}
      size="lg"
      className="invoice-proposal"
      footer={
        <div className="buttons slideout">
          <div>
            <button
              className="btn-slideout"
              disabled={busy}
              onClick={() => finish([leftText], 'info')}
              title="The invoice is not changed — the order and its POs are saved"
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">{proposals.length ? 'block' : 'check'}</span></div>
              <span className="btn-label">{proposals.length ? 'Leave the invoice as it is' : 'OK'}</span>
            </button>
          </div>
          {proposals.length > 0 && (
            <div>
              <button className="btn-slideout btn-slideout-primary" onClick={apply} disabled={busy}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'progress_activity' : 'task_alt'}</span>
                </div>
                <span className="btn-label">{busy ? 'Changing…' : `Apply these changes to ${numbers.join(', ')}`}</span>
              </button>
            </div>
          )}
        </div>
      }
    >
      <OfferBody offer={offer} busy={busy} error={error} />
    </Modal>
  );
}

export default InvoiceEditOfferDialog;
